/**
 * Top-of-app banner displayed only when BETA_MODE=true on the backend
 * (surfaced via user.beta_mode on /auth/me).
 *
 * Visibility rules:
 *   - Reads `user.beta_mode` — when the flag flips off after redeploy
 *     the banner disappears with no other code change
 *   - Dismissable via the X icon. Dismiss persists 7 days in
 *     localStorage (was sessionStorage = per-tab, which felt broken
 *     because closing one tab brought it back)
 *   - After 7 days the banner re-appears once, in case the beta
 *     status has changed in the meantime
 *
 * Two-layer header layout: the message is centred horizontally, the X
 * is positioned absolutely so its width doesn't push the text off
 * centre. The X has a 44×44 touch target on mobile (Apple HIG min).
 */
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'compass_beta_banner_dismissed';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Returns true if the user dismissed the banner within the cooldown.
const isWithinCooldown = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch { return false; }
};

const BetaBanner = () => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(false);
  // Tracks the active dismiss animation so we slide-up before unmount.
  const [closing, setClosing] = useState(false);

  useEffect(() => { setHidden(isWithinCooldown()); }, []);

  if (!user?.beta_mode || hidden) return null;

  const handleDismiss = () => {
    setClosing(true);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch {}
    // 220ms matches the CSS slide-up duration below.
    setTimeout(() => setHidden(true), 220);
  };

  return (
    <div
      className={`sticky top-0 z-[60] w-full border-b border-amber-500/30 bg-[#1a1409] text-amber-200 ${closing ? 'beta-banner-closing' : ''}`}
      style={{ overflow: 'hidden' }}
    >
      <style>{`
        .beta-banner-closing {
          animation: beta-banner-out 220ms ease-in forwards;
        }
        @keyframes beta-banner-out {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(-100%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .beta-banner-closing { animation: none; }
        }
      `}</style>
      <div className="relative px-4 py-2 max-w-screen-2xl mx-auto">
        <p className="text-center text-xs sm:text-sm leading-snug px-12">
          <span className="mr-1.5" aria-hidden>🚧</span>
          <span className="font-semibold text-amber-300">Compass est en bêta gratuite</span>
          {' · '}
          <span className="opacity-90">
            Merci de tester et partager tes retours à{' '}
            <a
              href="mailto:hello@compasstracker.com"
              className="underline decoration-amber-500/40 hover:decoration-amber-300 hover:text-amber-100"
            >
              hello@compasstracker.com
            </a>
          </span>
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Fermer la bannière"
          className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-amber-400/70 hover:text-amber-200 hover:bg-amber-500/10 rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default BetaBanner;
